import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Vibe Coding Agency — Build Your App With AI | Vibing Company";
const DESC =
  "A vibe coding agency of full-stack designers. We design your website or app, build it with AI in Lovable, and ship it — or rescue the one you started yourself.";

const FAQS = [
  {
    q: "What does a vibe coding agency actually do?",
    a: "We build software the same way you would with an AI tool — describing, generating, reviewing — except we've done it a few hundred times, we design the interface properly, and we know which of the AI's defaults will hurt you in production.",
  },
  {
    q: "Can you take over a project I started?",
    a: "Yes, and it's most of our work. Share the Lovable project, the repo, or the Cursor workspace; we read it, tell you honestly what shape it's in, and pick up from there.",
  },
  {
    q: "Will I be locked in to you?",
    a: "No. You keep the project, the code and the accounts. We deliberately leave things in a state you can keep prompting on yourself, and we'll coach your team to do exactly that.",
  },
  {
    q: "How fast is this?",
    a: "A focused build is usually days to a few weeks, not months. Small rescues often land in a single sitting.",
  },
  {
    q: "What does it cost?",
    a: "We scope each engagement to the job rather than publishing packages — it's a thirty-minute conversation to find out.",
  },
];

const BLOCKS = [
  {
    h: "Design first, then the stack",
    p: "We're UI/UX designers who learned the engineering. That order matters: the interface gets designed on purpose, and the database, auth and deployment underneath it are built by the same people, so nothing gets lost in a handoff.",
  },
  {
    h: "Built in the open tools you already use",
    p: "Lovable for full apps, Cursor and Claude Code for the long tail. Real React and TypeScript, your accounts, your repo. Nothing proprietary, nothing you can't take somewhere else.",
  },
  {
    h: "Rescue, not just greenfield",
    p: "Half-built apps that loop on the same error, published builds that don't match the preview, launches blocked by a security question nobody can answer. We do that work weekly.",
  },
  {
    h: "You leave able to keep going",
    p: "Every engagement ends with a project you can keep prompting on, and — if you want — a few sessions teaching your team how we prompt, review and ship.",
  },
];

export const Route = createFileRoute("/vibe-coding-agency")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Vibe Coding Agency — Build Your App With AI" },
      ...ogImageMeta("home", "Share card: vibe coding agency"),
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://vibing.inc/vibe-coding-agency" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/vibe-coding-agency" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "the Vibing Company",
          description: DESC,
          url: "https://vibing.inc/vibe-coding-agency",
          areaServed: "Worldwide",
          serviceType: "Vibe coding agency",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section
          aria-labelledby="a-h"
          className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12"
        >
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Vibe coding agency</p>
          <h1
            id="a-h"
            className="mt-6 font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            We vibe code for a living.
          </h1>
          <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
            Full-stack designers who build websites and apps with AI — designed properly, built in Lovable, shipped and handed back to you.
          </p>
          <div className="mt-10">
            <TalkToJonButton variant="dark" immersive />
          </div>
        </section>

        <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
          {BLOCKS.map((b) => (
            <section
              key={b.h}
              className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16"
            >
              <div className="md:col-span-4">
                <h2
                  className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
                >
                  {b.h}
                </h2>
              </div>
              <div className="md:col-span-8 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
                <p>{b.p}</p>
              </div>
            </section>
          ))}
        </article>

        <section
          aria-labelledby="a-faq"
          className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32"
        >
          <h2 id="a-faq" className="fluid-h2 font-display max-w-[18ch]">
            The usual questions.
          </h2>
          <dl className="mt-14 border-t border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
                <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">
                  {f.q}
                </dt>
                <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="fluid-h2 font-display max-w-[22ch] mx-auto">
            Tell us what you're building.
          </h2>
          <div className="mt-10 flex justify-center">
            <TalkToJonButton variant="dark" immersive />
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6">
            <Link to="/services" className="font-display text-electric underline underline-offset-4">
              → What we do
            </Link>
            <Link
              to="/guides/what-is-vibe-coding"
              className="font-display text-electric underline underline-offset-4"
            >
              → What is vibe coding?
            </Link>
            <Link
              to="/guides/vibe-coding-help"
              className="font-display text-electric underline underline-offset-4"
            >
              → Vibe coding help
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
